// One card per registered prompt_key. Behavior:
// - Always shows the active text (read-only when collapsed).
// - "Suggest edit" opens an inline editor; submit POSTs a suggestion.
// - "Edit directly" (apply-role + `prompt.can_direct_edit` only) opens the
//   same editor; submit upserts the org override. The server hides
//   `can_direct_edit` from trainers on prompts flagged as
//   `requiresSuggestion` (see dev-sync b02575e7 + server/prompts/catalog.ts).
// - "Revert to default" (apply-role only) deletes the override row.
// - "Versions" (apply-role + history present) toggles the slice-C history list.
// - Surfaces a source pill ("Override active" vs "Catalog default") and a
//   "Review required" pill on `requires_suggestion` prompts.
// - Editor body (textarea + validation feedback + buttons) lives in
//   `EditablePromptCardEditor`; the version-history list lives in
//   `EditablePromptCardVersions`.

import { useEffect, useState } from "react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { PromptRowDTO } from "@/lib/aiPrompting/client";
import {
  ReviewRequiredPill,
  SourcePill,
  TelemetryHealthPill,
} from "./EditablePromptCardPills";
import { EditablePromptCardEditor } from "./EditablePromptCardEditor";
import { EditablePromptCardVersions } from "./EditablePromptCardVersions";

export type EditablePromptCardProps = {
  prompt: PromptRowDTO;
  canApply: boolean;
  onSaveOverride: (text: string) => Promise<{ ok: true } | { ok: false; error: string }>;
  onRevertOverride: () => Promise<{ ok: true } | { ok: false; error: string }>;
  onCreateSuggestion: (
    text: string,
    note: string | null,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
  /**
   * Slice C: invoked after a successful Restore from version history. The
   * card itself doesn't own the restore API call (the child Versions
   * component does), but the parent panel's data hook needs to re-fetch
   * so the card's active_text + override row + history_count reflect the
   * restored state.
   */
  onRefresh: () => void | Promise<void>;
  /** When set, applied to the Suggest edit button (first card in tour). */
  suggestFireflyTargetId?: string;
};

type Mode = "view" | "suggest" | "edit";

export function EditablePromptCard({
  prompt,
  canApply,
  onSaveOverride,
  onRevertOverride,
  onCreateSuggestion,
  onRefresh,
  suggestFireflyTargetId,
}: EditablePromptCardProps) {
  const [mode, setMode] = useState<Mode>("view");
  const [draft, setDraft] = useState(prompt.active_text);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);

  useEffect(() => {
    if (mode !== "view") return;
    // Sync the draft with the active text when we re-enter view mode or when
    // the prop changes underneath us (e.g. after a publish / revert /
    // restore re-fetches the panel data). This is the intentional
    // "sync state with external value" idiom — same exemption pattern as
    // `src/hooks/useVoiceSettings.ts`.
    /* eslint-disable react-hooks/set-state-in-effect */
    setDraft(prompt.active_text);
    setError(null);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [mode, prompt.active_text]);

  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => setSuccess(null), 3500);
    return () => window.clearTimeout(t);
  }, [success]);

  const showDirectEdit = canApply && prompt.can_direct_edit;

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result =
      mode === "edit"
        ? await onSaveOverride(draft)
        : await onCreateSuggestion(draft, note.trim() ? note.trim() : null);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSuccess(mode === "edit" ? "Override saved." : "Sent for review — trainers will see this in the prompt review tray.");
    setMode("view");
    setNote("");
  };

  const revert = async () => {
    setBusy(true);
    setError(null);
    const r = await onRevertOverride();
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setSuccess("Reverted to catalog default.");
  };

  const cancelEdit = () => {
    setMode("view");
    setDraft(prompt.active_text);
    setNote("");
    setError(null);
  };

  return (
    <article className="rounded-xl border border-violet-100 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900/60">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-violet-900 dark:text-violet-100">{prompt.label}</h3>
            <SourcePill source={prompt.source} />
            {prompt.requires_suggestion ? <ReviewRequiredPill /> : null}
            <TelemetryHealthPill health={prompt.health} />
            {/* Slice F: 24h extract telemetry roll-up. Green when all
                recent runs succeeded, amber/rose when failures appear,
                neutral when no runs in window. */}
          </div>
          <p className="mt-1 text-xs text-gray-700 dark:text-zinc-300">{prompt.description}</p>
          {prompt.placeholders.length > 0 ? (
            <p className="mt-2 text-[11px] text-gray-500 dark:text-zinc-400">
              Tokens:{" "}
              {prompt.placeholders.map((p) => (
                <code key={p} className="mx-0.5 rounded bg-violet-50 px-1 py-0.5 text-violet-900 dark:bg-violet-950/40 dark:text-violet-200">
                  {p}
                </code>
              ))}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {mode === "view" ? (
            <>
              <button
                type="button"
                {...(suggestFireflyTargetId
                  ? { [FIREFLY_TARGET_ATTR]: suggestFireflyTargetId }
                  : {})}
                onClick={() => setMode("suggest")}
                className="text-xs rounded-md border border-violet-300 bg-violet-50 px-2.5 py-1 font-semibold text-violet-900 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/50 dark:text-violet-100"
              >
                Suggest edit
              </button>
              {showDirectEdit ? (
                <button
                  type="button"
                  onClick={() => setMode("edit")}
                  className="text-xs rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-900 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-100"
                >
                  Edit directly
                </button>
              ) : null}
              {canApply && prompt.source === "override" ? (
                <button
                  type="button"
                  onClick={revert}
                  disabled={busy}
                  className="text-xs rounded-md border border-gray-300 bg-white px-2.5 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                >
                  Revert to default
                </button>
              ) : null}
              {canApply && prompt.history_count > 0 ? (
                <button
                  type="button"
                  onClick={() => setVersionsOpen((o) => !o)}
                  className="text-xs rounded-md border border-gray-300 bg-white px-2.5 py-1 text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                  aria-expanded={versionsOpen}
                >
                  {versionsOpen ? "Hide versions" : `Versions (${prompt.history_count})`}
                </button>
              ) : null}
            </>
          ) : null}
        </div>
      </header>

      {mode === "view" ? (
        <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-violet-50 bg-violet-50/40 p-3 font-mono text-xs leading-relaxed text-gray-800 dark:border-zinc-700 dark:bg-zinc-900/40 dark:text-zinc-200">
          {prompt.active_text}
        </pre>
      ) : (
        <EditablePromptCardEditor
          mode={mode}
          activeText={prompt.active_text}
          placeholders={prompt.placeholders}
          draft={draft}
          setDraft={setDraft}
          note={note}
          setNote={setNote}
          busy={busy}
          onCancel={cancelEdit}
          onSubmit={submit}
          promptKey={prompt.prompt_key}
          dryRunEnabled={prompt.has_dry_run_fixture}
        />
      )}
      {versionsOpen && canApply && prompt.history_count > 0 ? (
        <EditablePromptCardVersions
          promptKey={prompt.prompt_key}
          promptLabel={prompt.label}
          // Re-key the list on every history_count change so a fresh
          // snapshot from a publish / revert / restore shows up without a
          // manual refresh from the user.
          refreshNonce={prompt.history_count}
          onRestored={() => {
            setSuccess("Version restored.");
            setVersionsOpen(false);
            void onRefresh();
          }}
        />
      ) : null}
      {error ? <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p> : null}
      {success ? <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">{success}</p> : null}
    </article>
  );
}
